import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, Star } from 'lucide-react';

const LOGO_DARK_URL = 'https://customer-assets.emergentagent.com/job_f9f64a4c-3451-4815-8703-6feb7c894858/artifacts/mema1897_Updated%20Logo.png';

export default function PortalPaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [reviewUrl, setReviewUrl] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    const token = localStorage.getItem('portal_token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    checkPayment();
    api.get('/trustpilot/public-config')
      .then((res) => setReviewUrl(res.data?.review_url || null))
      .catch(() => {});
  }, [sessionId]);

  const checkPayment = async () => {
    try {
      const res = await api.get(`/portal/payment-status/${sessionId}`);
      setPaymentData(res.data);
      setStatus(res.data.payment_status === 'paid' ? 'success' : 'pending');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full" data-testid="payment-result-card">
        <CardContent className="pt-8 pb-6 text-center space-y-4">
          <img src={LOGO_DARK_URL} alt="Helios" className="h-10 mx-auto mb-4" />

          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-blue-600 animate-spin" />
              <h2 className="text-xl font-semibold text-slate-900">Checking payment...</h2>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900">Payment Successful!</h2>
              <p className="text-slate-500">
                Thank you for your payment of <strong>£{((paymentData?.amount_total || 0) / 100).toFixed(2)}</strong>.
                A confirmation email has been sent.
              </p>

              {reviewUrl && (
                <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="trustpilot-review-prompt">
                  <p className="text-sm font-semibold text-slate-800">Enjoying your experience with Helios?</p>
                  <div className="flex items-center justify-center gap-1 my-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-6 h-6 fill-emerald-500 text-emerald-500" />
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mb-3">It only takes a minute and really helps our small team.</p>
                  <Button
                    asChild
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="leave-review-btn"
                  >
                    <a href={reviewUrl} target="_blank" rel="noopener noreferrer">
                      <Star className="w-4 h-4 mr-1.5" /> Leave us a review on Trustpilot
                    </a>
                  </Button>
                </div>
              )}
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900">Payment Processing</h2>
              <p className="text-slate-500">Your payment is being processed. Please check back shortly.</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900">Something went wrong</h2>
              <p className="text-slate-500">We couldn't verify your payment. Please contact Helios for assistance.</p>
            </>
          )}

          <Button
            onClick={() => navigate('/portal/dashboard')}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="return-to-portal-btn"
          >
            Return to Portal
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
